// MA-507 — the ONE word-normalization decode-support keys are built from.
//
// THE BUG THIS CLOSES (live-caught on prod): scripts/content/narratableStrings.mjs's
// enumerateDecodeSupportStrings() already lowercases every corpus word before it
// is ever handed to buildDecodeUtterance (`String(page.text || "").toLowerCase()`),
// so the generated manifest — and scripts/narrate/generate.mjs, which shares this
// same enumerator — only ever produces a LOWERCASE decode-support entry. The
// runtime lookup side (src/screens/StoryReader.tsx's library tap-a-word, a pure
// word replay) hashed the tapped token's RAW, case-preserving form instead
// (src/engine/wordTokenizer.ts's `spoken` deliberately keeps case — see its own
// T2d bite test — so this could never be "fixed" by changing the tokenizer). A
// sentence-initial/capitalized word ("Sure") therefore hashed to a DIFFERENT
// key than the "sure" entry the corpus actually has, silently missed the
// manifest, and fell back to the WebSpeech robot voice — while the identical
// word spoken mid-sentence (lowercase) played the real pregenerated audio.
//
// Both sides now derive their key through THIS one function — never a
// re-implemented `.toLowerCase()` copy that could silently drift from what
// generation actually did — so a capitalized/sentence-initial tap and its
// lowercase form are parity-by-construction, not by two authors independently
// getting the same answer today.
//
// Deliberately narrower than narrationHashKey.ts's `normalizeNarrationText`
// (whitespace/NFC collapse over full prose, case-preserving — right for a
// page/prompt, where case is part of the audio's real identity): a decode-
// support word is graded and segmented by decodabilityEngine on this exact
// lowercase-letters-only form (see decodeUtterance.ts's own `gradeWord`/
// `segment` calls), so the SAME reduction has to be the utterance key too —
// letters-only strips punctuation/apostrophes exactly as
// narratableStrings.mjs's page-word extraction already does (its `.match(/[a-z']+/g)`
// candidate keeps apostrophes only to then strip them again via
// `.replace(/[^a-z]/g, "")` — this function reproduces that same net result
// for a single already-tokenized word).
//
// ⭐MA-804 — APOSTROPHES ARE PART OF THE WORD. The letters-only reduction above
// turned "He'll" into "hell", "she'll" into "shell", "we'll" into "well",
// "I'll" into "ill", "she'd" into "shed", "we're" into "were", "fox's" into
// "foxs": a tap (and the recorded clip behind it) played a DIFFERENT real
// word. The key now keeps INTERNAL apostrophes ("he'll", "don't", "fox's"):
// every glyph the library types as an apostrophe — ' U+2019 U+2018 U+02BC
// U+00B4 and the backtick — is folded to the straight one, so all typings of
// one word share one key. Apostrophes at either END of the token (a quote mark
// wrapping the word: 'hello', ‘Hello’, dogs') are still stripped, exactly as
// they always were. A token with a digit before the apostrophe ("20's",
// "80's") keeps its digits too, so it replays as written, never as "s".
// Every other non-letter is dropped as before. buildDecodeUtterance routes any
// key containing an apostrophe to a whole-word replay (never a sound-out).

/** Every glyph treated as an apostrophe (shared with the library enumerator). */
export const APOSTROPHE_GLYPHS = "'\u2018\u2019\u02bc\u00b4`";

export function normalizeDecodeWord(word: string): string {
  const folded = word.trim().toLowerCase().replace(/[\u2018\u2019\u02bc\u00b4`]/g, "'");
  // A decade-style token ("20's"): digits adjacent to an internal apostrophe.
  const keep = /[0-9]'[a-z0-9]|[a-z0-9]'[0-9]/.test(folded) ? /[^a-z0-9']/g : /[^a-z']/g;
  return folded
    .replace(keep, "")
    .replace(/'{2,}/g, "'")
    .replace(/^'+|'+$/g, "");
}
